import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items);
      setUnread(data.unread);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    const refresh = () => load();
    window.addEventListener("focus", refresh);
    window.addEventListener("notifications-updated", refresh);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("notifications-updated", refresh);
    };
  }, []);

  const openItem = async (n) => {
    setOpen(false);
    try {
      await api.post("/notifications/read", { ids: [n.id] });
    } catch (e) {
      /* ignore */
    }
    load();
    navigate(`/projects/${n.project_id}`);
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read", {});
    } catch (e) {
      /* ignore */
    }
    load();
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button
          className="relative text-slate-500 hover:text-slate-900 transition-colors p-1"
          data-testid="notification-bell"
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center"
              data-testid="notification-count"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
          <span className="text-sm font-medium text-slate-900">Notifications</span>
          {unread > 0 && (
            <button
              onClick={markAll}
              className="text-xs text-slate-500 hover:text-slate-900 transition-colors"
              data-testid="notification-mark-all"
            >
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto" data-testid="notification-list">
          {items.length === 0 ? (
            <p className="text-sm text-slate-400 px-4 py-8 text-center">You're all caught up</p>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                onClick={() => openItem(n)}
                className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${
                  !n.read ? "bg-slate-50/70" : ""
                }`}
                data-testid={`notification-item-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-red-500 shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-sm text-slate-700">{n.text}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      {new Date(n.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
